/**
 * Expedição — romaneio de carga e MDF-e.
 *
 * O módulo existia inteiro na API (14 rotas) e não tinha tela nenhuma. Como
 * quase tudo aqui é liberado ao PATIO, era justamente o papel que mais precisa
 * ficando sem a ferramenta do próprio serviço: montar a carga do caminhão.
 *
 * Spec §2.7, explícita: romaneio NÃO aparece na razão. Ele agrupa o que sobe
 * no caminhão; quem tira do pátio é a nota. Por isso montar, refazer e
 * cancelar carga é livre — nada foi lançado.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api, podeTudoQue,
  type Filial, type Papel,
} from "../api.ts";
import { Campo, Selecao } from "../componentes/Campos.tsx";
import { BotaoImprimir, Impresso, situacaoImpressa } from "../componentes/Impresso.tsx";

/** As consultas vêm de SQL cru, então chegam em snake_case. */
interface LinhaRomaneio {
  id: string; numero: number; status: string;
  volume_total: string; peso_total_kg: string | null;
  uf_destino: string | null; municipio_destino: string | null;
  criado_em: string; fechado_em: string | null;
  placa: string | null; motorista: string | null;
  itens: string; mdfe: number | null;
}

interface ItemRomaneio {
  ordem: number; pedido_item_id: string;
  esp_mm: number | null; larg_mm: number | null; comp_m: string | null;
  pecas: number; volume_m3: string;
  codigo: string; especie: string;
  pedido: number; cliente: string;
  nfe: number | null; nfe_status: string | null;
}

interface Romaneio {
  id: string; numero: number; status: string;
  volume_total: string; peso_total_kg: string | null;
  uf_destino: string | null; municipio_destino: string | null;
  observacao: string | null;
  placa: string | null; capacidade_m3: string | null; motorista: string | null;
  mdfe_id: string | null; mdfe_numero: number | null; mdfe_chave: string | null;
  itens: ItemRomaneio[];
}

interface Disponivel {
  id: string;
  esp_mm: number | null; larg_mm: number | null; comp_m: string | null;
  pecas: number; volume_m3: string;
  codigo: string; especie: string;
  pedido: number; pedido_status: string; cliente: string;
}

interface Veiculo {
  id: string; placa: string; descricao: string | null;
  capacidadeM3: string | null; ativo: boolean;
}
interface Motorista { id: string; nome: string; cpf: string; ativo: boolean }

const n3 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const n0 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const dt = (v: string | null) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

const bitola = (i: { esp_mm: number | null; larg_mm: number | null; comp_m: string | null }) =>
  i.esp_mm === null ? "tora" : `${i.esp_mm} × ${i.larg_mm} × ${i.comp_m}`;

/** Os cinco estados do romaneio, na ordem em que a carga anda. */
function pilStatus(s: string) {
  const c =
    s === "ENTREGUE" ? "p-ok"
    : s === "EM_TRANSITO" ? "p-info"
    : s === "FECHADO" ? "p-info"
    : s === "MONTANDO" ? "p-aten"
    : "p-erro";
  return (
    <span className={`pil ${c}`}>
      <i />{s.toLowerCase().replace("_", " ")}
    </span>
  );
}

export function Expedicao({
  aviso, papel, filiais,
}: {
  aviso: (t: string) => void;
  papel: Papel;
  filiais: Filial[];
}) {
  const [lista, setLista] = useState<LinhaRomaneio[] | null>(null);
  const [status, setStatus] = useState("");
  const [aberto, setAberto] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  const recarregar = useCallback(() => {
    setLista(null);
    const q = status ? `?status=${status}` : "";
    void api.get<{ romaneios: LinhaRomaneio[] }>(`/romaneios${q}`)
      .then((r) => setLista(r.romaneios))
      .catch(() => setLista([]));
  }, [status]);
  useEffect(recarregar, [recarregar]);

  return (
    <>
      <div className="cab-doc">
        <h1>Expedição</h1>
        <Selecao
          rotulo="" valor={status} aoMudar={setStatus} largura={140}
          opcoes={[
            ["", "todas"], ["MONTANDO", "montando"],
            ["FECHADO", "fechadas"], ["CANCELADO", "canceladas"],
          ]}
        />
        <span className="cod">{lista?.length ?? 0} romaneios</span>
        <div style={{ marginLeft: "auto" }}>
          <button className="bt" onClick={() => setCriando((c) => !c)}>
            {criando ? "Fechar" : "Novo romaneio"}
          </button>
        </div>
      </div>

      <div className="rolagem">
        {criando && (
          <NovoRomaneio
            filiais={filiais}
            aviso={aviso}
            aoCriar={(id) => { setCriando(false); recarregar(); setAberto(id); }}
          />
        )}

        <div className="painel">
          {lista === null ? (
            <Esq />
          ) : lista.length === 0 ? (
            <div className="vazio">
              <p>Nenhum romaneio. Monte a primeira carga em “Novo romaneio”.</p>
            </div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>Nº</th>
                  <th>Situação</th>
                  <th>Veículo</th>
                  <th>Motorista</th>
                  <th>Destino</th>
                  <th style={{ textAlign: "right" }}>Itens</th>
                  <th style={{ textAlign: "right" }}>Volume m³</th>
                  <th style={{ textAlign: "right" }}>Peso kg</th>
                  <th>MDF-e</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.map((r) => (
                  <tr key={r.id}>
                    <td className="r">{r.numero}</td>
                    <td>{pilStatus(r.status)}</td>
                    <td className="mono">{r.placa ?? "—"}</td>
                    <td>{r.motorista ?? "—"}</td>
                    <td>
                      {r.municipio_destino
                        ? `${r.municipio_destino}/${r.uf_destino ?? "—"}`
                        : "—"}
                    </td>
                    <td className="r">{r.itens}</td>
                    <td className="r">{n3(r.volume_total)}</td>
                    <td className="r">{n0(r.peso_total_kg)}</td>
                    <td className="mono">{r.mdfe ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                        onClick={() => setAberto(aberto === r.id ? null : r.id)}
                      >
                        {aberto === r.id ? "fechar" : "abrir"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {aberto && (
          <Detalhe
            id={aberto} aviso={aviso} papel={papel} filiais={filiais}
            aoMudar={recarregar}
          />
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------ criação */

/**
 * Veículo e motorista são exigidos aqui, e não é capricho de formulário.
 *
 * `fecharRomaneio` recusa sem os dois, e não existe rota para defini-los
 * depois — só `POST /romaneios` aceita. Um romaneio criado sem eles nasce
 * impossível de fechar, e o operador só descobriria no fim, com o caminhão
 * carregado.
 */
function NovoRomaneio({
  filiais, aviso, aoCriar,
}: {
  filiais: Filial[];
  aviso: (t: string) => void;
  aoCriar: (id: string) => void;
}) {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [motoristas, setMotoristas] = useState<Motorista[]>([]);
  const [filialId, setFilialId] = useState(filiais[0]?.id ?? "");
  const [veiculoId, setVeiculoId] = useState("");
  const [motoristaId, setMotoristaId] = useState("");
  const [uf, setUf] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [observacao, setObservacao] = useState("");
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    void api.get<Veiculo[]>("/veiculos").then((v) => {
      setVeiculos(v);
      setVeiculoId((a) => a || v[0]?.id || "");
    }).catch(() => {});
    void api.get<Motorista[]>("/motoristas").then((m) => {
      setMotoristas(m);
      setMotoristaId((a) => a || m[0]?.id || "");
    }).catch(() => {});
  }, []);

  const semFrota = veiculos.length === 0 || motoristas.length === 0;

  async function criar() {
    setOcupado(true);
    try {
      const r = await api.post<{ id: string; numero: number }>("/romaneios", {
        filialId, veiculoId, motoristaId,
        ufDestino: uf || undefined,
        municipioDestino: municipio || undefined,
        observacao: observacao || undefined,
      });
      aviso(`Romaneio ${r.numero} aberto — adicione os itens da carga`);
      aoCriar(r.id);
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao criar romaneio");
    } finally { setOcupado(false); }
  }

  return (
    <div className="painel">
      <div className="tt"><span className="rotulo">Novo romaneio</span></div>
      {semFrota ? (
        <div className="vazio">
          <p>
            Sem veículo ou motorista cadastrado. A carga não fecha sem os dois —
            cadastre em <b>Cadastros → Frota</b> antes de montar o romaneio.
          </p>
        </div>
      ) : (
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Selecao
            rotulo="Filial" valor={filialId} aoMudar={setFilialId} largura={180}
            opcoes={filiais.map((f) => [f.id, f.nome] as const)}
          />
          <Selecao
            rotulo="Veículo" valor={veiculoId} aoMudar={setVeiculoId} largura={190}
            opcoes={veiculos.map((v) => [
              v.id,
              v.capacidadeM3 ? `${v.placa} · ${n3(v.capacidadeM3)} m³` : v.placa,
            ] as const)}
          />
          <Selecao
            rotulo="Motorista" valor={motoristaId} aoMudar={setMotoristaId} largura={190}
            opcoes={motoristas.map((m) => [m.id, m.nome] as const)}
          />
          <Campo rotulo="UF destino" largura={80} valor={uf} aoMudar={(v) => setUf(v.toUpperCase())} />
          <Campo rotulo="Município destino" largura={190} valor={municipio} aoMudar={setMunicipio} />
          <Campo rotulo="Observação" largura={220} valor={observacao} aoMudar={setObservacao} />
          <button className="bt" onClick={() => void criar()} disabled={ocupado || !filialId}>
            Abrir romaneio
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ detalhe */

function Detalhe({
  id, aviso, papel, filiais, aoMudar,
}: {
  id: string;
  aviso: (t: string) => void;
  papel: Papel;
  filiais: Filial[];
  aoMudar: () => void;
}) {
  const [r, setR] = useState<Romaneio | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /** Cancelar carga é do escritório — a rota exige, o botão precisa dizer. */
  const podeCancelar = podeTudoQue(papel, "ESCRITORIO");

  const carregar = useCallback(() => {
    setR(null);
    void api.get<Romaneio>(`/romaneios/${id}`).then(setR).catch(() => setR(null));
  }, [id]);
  useEffect(carregar, [carregar]);

  const filial = useMemo(
    () => filiais.find((f) => f.matriz) ?? filiais[0] ?? null,
    [filiais],
  );

  if (!r) return <div className="painel"><Esq /></div>;

  const montando = r.status === "MONTANDO";

  async function acao(caminho: string, texto: string) {
    setOcupado(true);
    try {
      await api.post(caminho, {});
      aviso(texto);
      carregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha na operação");
    } finally { setOcupado(false); }
  }

  async function removerItem(pedidoItemId: string) {
    try {
      await api.del(`/romaneios/${id}/itens/${pedidoItemId}`);
      aviso("Item retirado da carga");
      carregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao retirar item");
    }
  }

  /* Capacidade: avisa ANTES de tentar fechar, que é quando ainda dá para
     tirar uma peça em vez de descarregar o caminhão. */
  const cabe = r.capacidade_m3 === null
    ? null
    : Number(r.volume_total) <= Number(r.capacidade_m3);

  return (
    <div className="painel">
      <div className="tt" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span className="rotulo">Romaneio {r.numero}</span>
        {pilStatus(r.status)}
        <span className="cod">
          {r.placa ?? "sem veículo"} · {r.motorista ?? "sem motorista"}
          {r.municipio_destino && ` · ${r.municipio_destino}/${r.uf_destino ?? ""}`}
        </span>
        {r.mdfe_numero !== null && (
          <span className="pil p-ok"><i />MDF-e {r.mdfe_numero}</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <BotaoImprimir rotulo="Imprimir romaneio" />
          {montando && (
            <button
              className="bt" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void acao(`/romaneios/${id}/fechar`, "Carga fechada")}
              disabled={ocupado || r.itens.length === 0}
            >
              Fechar carga
            </button>
          )}
          {r.status === "FECHADO" && r.mdfe_numero === null && (
            <button
              className="bt" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void acao(`/romaneios/${id}/manifesto`, "MDF-e emitido")}
              disabled={ocupado}
            >
              Emitir MDF-e
            </button>
          )}
          {r.status === "EM_TRANSITO" && r.mdfe_id !== null && (
            <button
              className="bt" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void acao(
                `/manifestos/${r.mdfe_id}/encerrar`,
                "Entrega registrada · MDF-e encerrado",
              )}
              disabled={ocupado}
            >
              Registrar entrega
            </button>
          )}
          {r.status !== "CANCELADO" && r.status !== "ENTREGUE" && (
            <button
              className="bt bt-destrutivo" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void acao(`/romaneios/${id}/cancelar`, "Romaneio cancelado")}
              disabled={!podeCancelar || ocupado}
              title={podeCancelar ? undefined : "Cancelar carga é do escritório"}
            >
              Cancelar
            </button>
          )}
        </div>
      </div>

      <div className="metricas">
        <div>
          <span className="rotulo">Volume</span>
          <span className="vl">{n3(r.volume_total)} m³</span>
          <span className="ct">
            {r.capacidade_m3
              ? `de ${n3(r.capacidade_m3)} m³ do veículo`
              : "veículo sem capacidade cadastrada"}
          </span>
        </div>
        <div>
          <span className="rotulo">Peso estimado</span>
          <span className="vl">{n0(r.peso_total_kg)} kg</span>
          <span className="ct">pela densidade da espécie</span>
        </div>
        <div>
          <span className="rotulo">Itens</span>
          <span className="vl">{r.itens.length}</span>
          <span className="ct">de pedido nesta carga</span>
        </div>
      </div>

      {cabe === false && (
        <div style={{
          margin: 14, padding: 12, borderRadius: 4,
          background: "var(--erro-fundo)", border: "1px solid var(--erro-borda)",
          color: "var(--erro-texto)", fontSize: 13.5,
        }}>
          A carga de <b>{n3(r.volume_total)} m³</b> passa da capacidade de{" "}
          <b>{n3(r.capacidade_m3)} m³</b> do {r.placa}. Retire itens — fechar
          vai ser recusado.
        </div>
      )}

      <table className="tab">
        <thead>
          <tr>
            <th style={{ textAlign: "right" }}>#</th>
            <th style={{ textAlign: "right" }}>Pedido</th>
            <th>Cliente</th>
            <th>Produto</th>
            <th>Bitola</th>
            <th style={{ textAlign: "right" }}>Peças</th>
            <th style={{ textAlign: "right" }}>Volume m³</th>
            <th>NF-e</th>
            {montando && <th />}
          </tr>
        </thead>
        <tbody>
          {r.itens.length === 0 ? (
            <tr>
              <td colSpan={montando ? 9 : 8} style={{ color: "var(--texto-terciario)" }}>
                Carga vazia — escolha os itens abaixo.
              </td>
            </tr>
          ) : r.itens.map((i) => (
            <tr key={i.pedido_item_id}>
              <td className="r">{i.ordem}</td>
              <td className="r mono">{i.pedido}</td>
              <td>{i.cliente}</td>
              <td className="mono">{i.codigo}</td>
              <td className="mono">{bitola(i)}</td>
              <td className="r">{i.pecas.toLocaleString("pt-BR")}</td>
              <td className="r">{n3(i.volume_m3)}</td>
              <td>
                {i.nfe === null
                  ? <span className="pil p-aten"><i />sem nota</span>
                  : <span className="pil p-ok"><i />{i.nfe}</span>}
              </td>
              {montando && (
                <td style={{ textAlign: "right" }}>
                  <button
                    className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                    onClick={() => void removerItem(i.pedido_item_id)}
                  >
                    retirar
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {montando && (
        <Disponiveis
          romaneioId={id}
          aviso={aviso}
          aoAdicionar={() => { carregar(); aoMudar(); }}
        />
      )}

      {/* Romaneio de carga: é o papel que viaja com o caminhão. */}
      <Impresso
        emitente={{
          nome: filial?.razaoSocial ?? "—",
          cnpj: filial?.cnpj ?? "—",
          filial: filial?.nome ?? "—",
          cidade: filial?.cidade ?? "—",
          uf: filial?.uf ?? "—",
        }}
        titulo="Romaneio de carga"
        numero={String(r.numero)}
        emissao={dt(new Date().toISOString())}
        rodapeEsquerda={`Veículo ${r.placa ?? "—"} · Motorista ${r.motorista ?? "—"}`}
        rodapeDireita={`Situação: ${situacaoImpressa(r.status)}`}
        assinaturas={["Conferente", "Motorista"]}
      >
        <div className="doc-partes">
          <div>
            <span className="rot">Transporte</span>
            <b>{r.placa ?? "—"}</b><br />
            Motorista: {r.motorista ?? "—"}
          </div>
          <div>
            <span className="rot">Destino</span>
            <b>
              {r.municipio_destino
                ? `${r.municipio_destino}/${r.uf_destino ?? "—"}`
                : "—"}
            </b>
            {r.mdfe_numero !== null && <><br />MDF-e {r.mdfe_numero}</>}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th><th>Pedido</th><th>Cliente</th>
              <th>Produto</th><th>Bitola</th>
              <th style={{ textAlign: "right" }}>Peças</th>
              <th style={{ textAlign: "right" }}>Volume m³</th>
            </tr>
          </thead>
          <tbody>
            {r.itens.map((i) => (
              <tr key={i.pedido_item_id}>
                <td className="n">{i.ordem}</td>
                <td className="n">{i.pedido}</td>
                <td>{i.cliente}</td>
                <td>{i.codigo}</td>
                <td>{bitola(i)}</td>
                <td className="n">{i.pecas.toLocaleString("pt-BR")}</td>
                <td className="n">{n3(i.volume_m3)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>
                {r.itens.length} item(ns) · peso estimado {n0(r.peso_total_kg)} kg
              </td>
              <td />
              <td className="n">{n3(r.volume_total)}</td>
            </tr>
          </tfoot>
        </table>
      </Impresso>
    </div>
  );
}

/* -------------------------------------------------------- disponíveis */

/**
 * Itens de pedido que ainda não estão em nenhuma carga.
 *
 * Um item entra em UM romaneio só — o índice único do banco garante. Sem
 * isso a mesma madeira apareceria em dois caminhões e a conferência viraria
 * adivinhação. Por isso esta lista some o item assim que ele é embarcado.
 */
function Disponiveis({
  romaneioId, aviso, aoAdicionar,
}: {
  romaneioId: string;
  aviso: (t: string) => void;
  aoAdicionar: () => void;
}) {
  const [itens, setItens] = useState<Disponivel[] | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(() => {
    setItens(null);
    void api.get<{ itens: Disponivel[] }>("/romaneios/disponiveis")
      .then((r) => { setItens(r.itens); setMarcados(new Set()); })
      .catch(() => setItens([]));
  }, []);
  useEffect(carregar, [carregar]);

  const total = useMemo(
    () => (itens ?? [])
      .filter((i) => marcados.has(i.id))
      .reduce((s, i) => s + Number(i.volume_m3), 0),
    [itens, marcados],
  );

  function alternar(id: string) {
    setMarcados((m) => {
      const n = new Set(m);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function adicionar() {
    setOcupado(true);
    try {
      const r = await api.post<{ itens: number }>(`/romaneios/${romaneioId}/itens`, {
        pedidoItemIds: [...marcados],
      });
      aviso(`${marcados.size} item(ns) embarcados — carga com ${r.itens} no total`);
      carregar(); aoAdicionar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao embarcar itens");
    } finally { setOcupado(false); }
  }

  return (
    <div style={{ borderTop: "1px solid var(--borda-forte)" }}>
      <div className="tt" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="rotulo">Itens disponíveis para embarcar</span>
        <span className="cod">de pedidos abertos ou faturados, ainda sem carga</span>
        {marcados.size > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <span className="num" style={{ fontWeight: 600 }}>
              {marcados.size} marcados · {n3(String(total))} m³
            </span>
            <button className="bt" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void adicionar()} disabled={ocupado}>
              Embarcar
            </button>
          </div>
        )}
      </div>

      {itens === null ? (
        <Esq />
      ) : itens.length === 0 ? (
        <div className="vazio">
          <p>Nenhum item livre. Todo item de pedido já está em alguma carga.</p>
        </div>
      ) : (
        <table className="tab">
          <thead>
            <tr>
              <th style={{ width: 36 }} />
              <th style={{ textAlign: "right" }}>Pedido</th>
              <th>Cliente</th>
              <th>Produto</th>
              <th>Bitola</th>
              <th style={{ textAlign: "right" }}>Peças</th>
              <th style={{ textAlign: "right" }}>Volume m³</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i) => (
              <tr
                key={i.id}
                onClick={() => alternar(i.id)}
                style={{
                  cursor: "pointer",
                  background: marcados.has(i.id) ? "var(--acao-tinta)" : undefined,
                }}
              >
                <td>
                  <input
                    type="checkbox" checked={marcados.has(i.id)}
                    onChange={() => alternar(i.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="r mono">{i.pedido}</td>
                <td>{i.cliente}</td>
                <td className="mono">{i.codigo}</td>
                <td className="mono">{bitola(i)}</td>
                <td className="r">{i.pecas.toLocaleString("pt-BR")}</td>
                <td className="r">{n3(i.volume_m3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Esq() {
  return (
    <div className="esq">
      {[0, 1, 2].map((i) => (
        <div className="l" key={i}>
          <span className="b" /><span className="b" /><span className="b" /><span className="b" />
        </div>
      ))}
    </div>
  );
}
